import React, { FC, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  useGetStrategies,
  useUpdateStrategy,
  useDeleteStrategy,
  useCreateStrategy,
  Strategy as StrategyModel,
  StrategyFilter,
  getGetStrategiesQueryKey,
  useGetUserPreferences,
} from "@api";

import { Text, Button, Box, Divider, Notification } from "@ui/elements";
import { Stack } from "@ui/layout";

import {
  createFiltersFromStrategy,
  Filter,
  StrategyForm,
  toaster,
  useStrategyForm,
} from "@components";

import { ErrorType } from "../../../../services/api/custom-instance";

import style from "./Strategy.module.scss";

export const Strategy = () => {
  const { t } = useTranslation();
  const { data: strategies, refetch } = useGetStrategies();
  const { data: userPreferenceData, isLoading: userPreferencesLoading } =
    useGetUserPreferences();
  const hasSubscription = !!userPreferenceData?.hasActiveSubscription;
  const {
    mutateAsync: createStrategy,
    isPending: createStrategyLoading,
    error: createStrategyError,
  } = useCreateStrategy();
  const [editingStrategyId, setEditingStrategyId] = useState<string | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const handleEdit = (id: string) => {
    setEditingStrategyId(id);
    setIsCreating(false);
  };

  const handleCancelEdit = () => {
    setEditingStrategyId(null);
  };

  const handleCreate = () => {
    setEditingStrategyId(null);
    setIsCreating(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  const handleCreateSubmit = async (values: StrategyFormValues) => {
    const filtersToSave = values.filters
      .filter((e) => e.enabled)
      .map((filter) => {
        const { id, value, type } = filter;
        return { id, value, type } as StrategyFilter;
      });

    await createStrategy({
      data: { name: values.name, filters: filtersToSave },
    });
    toaster.success({
      title: t("messages.strategy-created"),
      message: t("messages.strategy-created-success"),
    });
    setIsCreating(false);
    refetch();
  };

  return (
    <Stack direction="column" gap="md">
      <Stack direction="column" gap="xs">
        <Text variant="secondary">{t("strategy-info-1")}</Text>
        <Text variant="secondary">{t("strategy-info-2")}</Text>
      </Stack>

      {!userPreferencesLoading && !hasSubscription && (
        <Notification color="warning">
          <p
            dangerouslySetInnerHTML={{
              __html: t("strategy-unsubscribed-warning"),
            }}
          />
        </Notification>
      )}

      {strategies?.map((strategy) => (
        <StrategyItem
          key={strategy.id}
          strategy={strategy}
          isEditing={editingStrategyId === strategy.id}
          onEdit={() => handleEdit(strategy.id)}
          onCancelEdit={handleCancelEdit}
        />
      ))}

      {!isCreating && (
        <Stack justify="end">
          <Button onClick={handleCreate} variant="light">
            {t("labels.create-strategy")}
          </Button>
        </Stack>
      )}

      {isCreating && (
        <CreateStrategyForm
          isLoading={createStrategyLoading}
          onSubmit={handleCreateSubmit}
          onCancel={handleCancelCreate}
          error={createStrategyError}
        />
      )}
    </Stack>
  );
};

type StrategyItemProps = {
  strategy: StrategyModel;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
};

const StrategyItem: FC<StrategyItemProps> = ({
  strategy,
  isEditing,
  onEdit,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: deleteStrategy, isPending: deleteStrategyLoading } =
    useDeleteStrategy();
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateStrategy,
    isPending: updateStrategyLoading,
    error: updateStrategyError,
  } = useUpdateStrategy();

  const handleDelete = async () => {
    try {
      await deleteStrategy({ id: strategy.id });
      toaster.success({
        title: t("messages.strategy-deleted"),
        message: t("messages.strategy-deleted-success"),
      });
      queryClient.invalidateQueries({
        queryKey: getGetStrategiesQueryKey(),
      });
    } catch (error) {
      toaster.error({
        title: t("messages.strategy-delete-failed"),
        message: t("messages.strategy-delete-failed-info"),
      });
    }
  };

  const onSubmit = async (values: StrategyFormValues) => {
    const filtersToSave = values.filters
      .filter((e) => e.enabled)
      .map((filter) => {
        const { id, value, type } = filter;
        return { id, value, type } as StrategyFilter;
      });

    await updateStrategy({
      data: { id: values.id || "", name: values.name, filters: filtersToSave },
    });
    toaster.success({
      title: t("messages.strategy-updated"),
      message: t("messages.strategy-updated-info"),
    });
    onCancelEdit();
    queryClient.invalidateQueries({
      queryKey: getGetStrategiesQueryKey(),
    });
  };

  const form = useStrategyForm({
    defaultValues: {
      name: strategy.name,
      filters: createFiltersFromStrategy(strategy),
    },
  });

  useEffect(() => {
    if (strategy) {
      form.setValue("id", strategy.id);
      form.setValue("name", strategy.name);
      form.setValue("filters", createFiltersFromStrategy(strategy));
    }
  }, [strategy]);

  return (
    <Box isBordered>
      <Stack direction="column" gap="md">
        {isEditing ? (
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack direction="column" gap="md">
              <StrategyForm form={form} updateError={updateStrategyError} />
              <Divider />
              <Stack justify="end">
                <Button variant="text" onClick={onCancelEdit}>
                  {t("labels.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  isLoading={updateStrategyLoading}
                >
                  {t("labels.update")}
                </Button>
              </Stack>
            </Stack>
          </form>
        ) : (
          <Stack direction="row" align="center" justify="between">
            <Stack
              direction="column"
              gap="none"
              className={style.strategyNameWrapper}
            >
              <Text weight="bold">{strategy.name}</Text>
              <Text variant="secondary">
                <span>
                  {dayjs(strategy.updated_at).format("DD MMM - HH:mm")}
                </span>
              </Text>
            </Stack>
            <Stack>
              <Button
                variant="text"
                color="danger"
                onClick={handleDelete}
                isLoading={deleteStrategyLoading}
              >
                {t("labels.delete")}
              </Button>
              <Button variant="light" color="info" onClick={onEdit}>
                {t("labels.edit")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

type StrategyFormValues = { name: string; filters: Filter[]; id?: string };

type StrategyFormProps = {
  onSubmit: (data: StrategyFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: ErrorType<unknown> | null;
};

const CreateStrategyForm: FC<StrategyFormProps> = ({
  onSubmit,
  onCancel,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const form = useStrategyForm();

  return (
    <Box isBordered>
      <Stack direction="column" gap="xl">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack direction="column" gap="md">
            <StrategyForm form={form} createError={error} />
            <Divider />
            <Stack justify="end">
              <Button variant="text" onClick={onCancel}>
                {t("labels.cancel")}
              </Button>
              <Button type="submit" color="primary" isLoading={isLoading}>
                {t("labels.create")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Box>
  );
};
